class ImageGenerationModal {
  constructor(modalElement, closeButtons = []) {
    this.modal = modalElement;
    this.modalContent = this.modal.querySelector('[data-img-gen-modal-content]');
    this.closeButtons = closeButtons;

    this.handleEsc = this.handleEsc.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.close = this.close.bind(this);
  }

  open() {
    const body = document.body;
    body.classList.add('drawer-active');
    this.modal.style.display = 'flex';
    this.modalContent.classList.add('is-open');

    window.addEventListener('keydown', this.handleEsc);
    window.addEventListener('click', this.handleClickOutside);
    this.closeButtons.forEach(btn => btn.addEventListener('click', this.close));
  }

  close() {
    this.modal.classList.add('close-image-generation-modal-window');
    this.modalContent.classList.add('is-close');

    window.removeEventListener('keydown', this.handleEsc);
    window.removeEventListener('click', this.handleClickOutside);
    this.closeButtons.forEach(btn => btn.removeEventListener('click', this.close));

    setTimeout(() => {
      this.modal.style.display = 'none';
      this.modalContent.classList.remove('is-close', 'is-open');
      this.modal.classList.remove('close-image-generation-modal-window');
      document.body.classList.remove('drawer-active');
    }, 500);
  }

  handleEsc(event) {
    if (event.key === 'Escape') this.close();
  }

  handleClickOutside(event) {
    if (event.target === this.modal) this.close();
  }
}

class ImageGeneration extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector("[data-image-generation]");
    this.imageGenerationPreview = this.querySelector('[data-image-generation-preview]')
    this.generatedImageBlob = null
    this.formData = null
    this.emailForm = ''
    this.errorMessageBlock = this.querySelector('[data-error-message-block]')
    this.imageGenerationContent = this.querySelector('[data-img-gen-content]')
    this.textarea =  this.querySelector('[data-img-gen-textarea]');
    this.modal = document.querySelector('[data-modal-window]');

    this.initEmailForm = false
  }

  connectedCallback() {    
    if(!this.form) return
    this.handleSubmitForm()

    if(this.textarea) {
      this.textarea.addEventListener('input', () => autoResize(this.textarea));
    }

    const modalElement = document.querySelector('[data-modal-window]');
    const closeBtns = [...modalElement.querySelectorAll(['[data-modal-close]'])]
    this.modal = new ImageGenerationModal(modalElement, closeBtns);

    this.addEventListener('initEmailForm', (e) => this.handleActionButtons(e.detail.isInitialized));
  }
  
  handleSubmitForm() {
    this.form.addEventListener('submit', async(e) => {
      e.preventDefault()

      const submitBtn =
        e.submitter ||
        this.form.querySelector('button[type="submit"], input[type="submit"]');

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
        submitBtn.classList.add('is-loading');
      }

      this.formData = new FormData(this.form);
  
      if(!this.formData) return

      const prompt = this.formData.get('prompt');
      if (!prompt) return;

      const payload = {
        prompt,
        honeypot: this.formData.get('honeypot'),
        generatedImage: this.generatedImageBlob ? await this.blobToBase64(this.generatedImageBlob) : ''
      }

      if(!this.classList.contains('initialized')) this.classList.add('initialized');
      this.errorMessageBlock.innerHTML = ''
      this.startLoading()
      this.handleScrollTo(this.imageGenerationContent)

      try {
        const response = await fetch('https://image-generation-app-w42sd.ondigitalocean.app/api/get-ai-image', {
          method: 'POST',
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        })

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Unknown server error");
        }

        const blob = await response.blob();
        const file = new File([blob], `image-generation.png`, { type: "image/png" });
        const url = URL.createObjectURL(file);
        
        this.endLoading()
        this.imageGenerationPreview.src = url
        this.generatedImageBlob = blob
        
        if(!this.initEmailForm) {
          this.initEmailForm = !this.initEmailForm 
          this.renderEmailForm()
        }

      } catch (error) {
        console.error(error.message);
        this.errorMessageBlock.innerHTML = error.message;
        this.clearly()
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.removeAttribute('aria-busy');
          submitBtn.classList.remove('is-loading');
        }
      }
    })
  }

  emailSubmitHandler = async(e) => {
    e.preventDefault()

    const submitBtn =
      e.submitter ||
      this.emailForm?.querySelector('button[type="submit"], input[type="submit"]');

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.classList.add('is-loading');
    }

    const formData = new FormData(this.emailForm);
    const email = formData.get('email');
    const preferences = formData.get('preferences')

    const total_sum = this.querySelector('.image-generation__total-sum').textContent || ''

    const fullFormData = new FormData();
    fullFormData.append('email', email);
    fullFormData.append('preferences', preferences);
    fullFormData.append('total_sum', total_sum);

    const base64 = await this.blobToBase64(this.generatedImageBlob);
    fullFormData.append('image_base64', base64);

    for (const [key, value] of this.formData.entries()) {
      fullFormData.append(key, value);
    }

    try {
      const response = await fetch('https://image-generation-app-w42sd.ondigitalocean.app/api/get-email', {
        method: 'POST',
        body: fullFormData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Unknown server error");
      }

      this.modal.open();

      const source = await response.json()
    } catch (error) {
      console.error(error.message);
      this.errorMessageBlock.innerHTML = error.message
    } finally {
      this.clearly();
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-busy');
        submitBtn.classList.remove('is-loading');
      }
    }
  }

  renderEmailForm() {
    const template = document.getElementById("imageGenerationEmailFormTemplate");
    const clone = template.content.cloneNode(true);
    this.appendChild(clone);
    this.emailForm = this.querySelector('[data-email-form]');
    this.emailForm.addEventListener('submit', this.emailSubmitHandler)

    this.initActionButtons(true)
  }

  handleScrollTo(scrollElement) {
    const offset = 120;
    const elementTop =
      scrollElement.getBoundingClientRect().top + window.pageYOffset;

    window.scrollTo({
      top: elementTop - offset,
      behavior: 'smooth'
    });
  }

  scrollToTop() {
    requestAnimationFrame(() => {
      this.parentElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  }

  startLoading() {
    this.classList.remove('complete')
    this.classList.add('loading')

  }

  endLoading() {
    this.classList.remove('loading')
    this.classList.add('complete')
  }

  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      if (!(blob instanceof Blob)) {
        reject(new Error('Invalid blob'));
        return;
      }

      const reader = new FileReader();

      reader.onloadend = () => {
        const result = reader.result;
        const base64 = result.split(',')[1];
        resolve(base64);
      };

      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  initActionButtons(boolean) {
    const initFormEvent = new CustomEvent('initEmailForm', {
      detail: { isInitialized : boolean },
      bubbles: true, 
    });

    this.dispatchEvent(initFormEvent);
  }

  handleActionButtons(isInitialized) {
    const arrayActionButtons = [...this.querySelectorAll('button[data-action]')]

    const handler = (event) => {
      const action = event.currentTarget.dataset.action;
      switch (action) {
        case 'restart':
          this.resetAndScrollToTop()
          break;
        case 'edit':
          this.enterEditMode();
          break;
        default:
          console.log('Unknown action:', action);
      }
    };

    arrayActionButtons.forEach(button => {
      if (isInitialized) {
        button.addEventListener('click', handler);
      } else {
        button.removeEventListener('click', handler);
      }
    });
  }

  resetAndScrollToTop() {
    this.clearly() 
    this.scrollToTop()
  }

  enterEditMode() {
    this.scrollToTop()
    requestAnimationFrame(() => {
    if (!this.textarea) return;

      this.textarea.focus({ preventScroll: true });
      const len = this.textarea.value.length;
      this.textarea.setSelectionRange(len, len);
      this.textarea.classList.remove('is-highlighted');
      this.textarea.offsetWidth;
      this.textarea.classList.add('is-highlighted');
    });
  }

  clearly() {
    this.classList.remove('initialized','loading','complete');
    if (this.emailForm && this.emailForm instanceof HTMLFormElement) {
      this.initActionButtons(false)
      this.emailForm.removeEventListener('submit', this.emailSubmitHandler);
    }
    this.initEmailForm = false
    this.formData = null
    if(this.emailForm) this.emailForm.remove()
    this.generatedImageBlob = null
  }
}

if (!customElements.get('image-generation')) {
  customElements.define('image-generation', ImageGeneration);
}

const autoResize = (el) => {

  if (!el.value.trim()) {
    el.style.height = '';
    return;
  }

  el.style.height = 'auto';
  el.style.height = el.scrollHeight -12 + 'px';
};


class ImageGenerationCalculator extends HTMLElement {
  constructor() {
    super()

    this.widthInput = this.querySelector('[name="wall_width"]')
    this.heightInput = this.querySelector('[name="wall_height"]')
    this.totalSumEl = this.querySelector('.image-generation__total-sum')
  }

  connectedCallback() {
    if (!this.widthInput || !this.heightInput || !this.totalSumEl) return

    this.handleInput = this.handleInput.bind(this)

    this.widthInput.addEventListener('input', this.handleInput)
    this.heightInput.addEventListener('input', this.handleInput)
  }

  handleInput(event) {
    const input = event.target

    input.value = input.value
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1')

    const width = parseFloat(this.widthInput.value)
    const height = parseFloat(this.heightInput.value)

    if (!width || !height || width < 0 || height < 0) {
      this.totalSumEl.textContent = '$0.00'
      return
    }

    const total = (width / 12) * (height / 12) *10
    this.totalSumEl.textContent = `$${total.toFixed(2)}`
  }
}

if (!customElements.get('image-generation-calculator')) {
  customElements.define('image-generation-calculator',ImageGenerationCalculator)
}